// The resolved proposal document: a template's blocks with every value filled in.
// Units are PDF points.

export interface DocumentField {
  slot: string;
  label?: string;
  value: string | null;
  readOnly?: boolean;
  source?: string;
  multiline?: boolean;
  needsInput?: boolean;
  missing?: boolean;
}

export interface DocumentLink {
  link: string;
  text: string;
}

export type Inline = string | DocumentField | DocumentLink;

export interface TextStyle {
  font: string;
  size: number;
  color: string;
  leading?: number;
  spaceBefore?: number;
  spaceAfter?: number;
  keepSpace?: number;
  link?: string;
  // A heading, paragraph or script in this style sits on a band of this colour across the frame.
  fill?: string;
  fillPadding?: number;
}

export interface HeadingStyle extends TextStyle {
  // Headings in this style are numbered "N.M" within their section.
  numbered?: boolean;
  bar?: string;
  barWidth?: number;
  barHeight?: number;
  indent?: number;
  rule?: string;
  ruleGap?: number;
}

export interface BulletsTheme {
  indent: number;
  dot: { x: number; radius: number; rise: number; color: string };
  spaceBefore?: number;
  // Below every item, so it also sets the least space between two items.
  spaceAfter?: number;
  itemSpace?: number;
}

export interface TableTheme {
  font: string;
  headFont: string;
  size: number;
  color: string;
  headColor: string;
  rowHeight: number;
  padding: number;
  headFill?: string;
  totalFill?: string;
  // The total row's font and colour, else the header's.
  totalFont?: string;
  totalColor?: string;
  rule?: string;
  spaceBefore?: number;
  spaceAfter?: number;
}

export interface CardTheme {
  fill?: string;
  border?: string;
  borderWidth?: number;
  padding: number;
  columnPitch: number;
  columnWidth: number;
  band?: {
    fill: string;
    height: number;
    labelOffset: number;
    valueOffset: number;
    label: TextStyle;
    value: TextStyle;
  };
  title: TextStyle & { offset: number; offsetAfterDivider: number };
  label: TextStyle & { afterTitle: number; afterValue: number };
  value: TextStyle & { leading: number; afterLabel: number; descent: number };
  divider: { color: string; width: number; gap: number };
  bottomPadding?: number;
  spaceBefore?: number;
  spaceAfter?: number;
}

export interface SignatureTheme {
  line: string;
  lineWidth: number;
  lineLength: number;
  pitch: number;
  // How far a label's baseline sits under its line; negative puts it above.
  labelGap: number;
  // A label beside its line: the line starts this far right of the label.
  labelWidth?: number;
  // A negative `below` puts the box above its line, growing the block at the top; `textHeight` only sizes the PDF's e-sign box for a name or title.
  box: { width: number; height: number; below: number; textHeight?: number };
  // Signature blocks that follow one another share a row, this many to a row, columnPitch apart.
  columns?: number;
  columnPitch?: number;
  // A signer's title: its baseline `size` below the top, or fillPadding inside a band, then `gap` to the fields.
  title?: TextStyle & { gap: number };
  spaceBefore?: number;
  spaceAfter?: number;
}

interface ComponentThemes {
  bullets: BulletsTheme;
  table: TableTheme;
  card: CardTheme;
  signature: SignatureTheme;
}

// A map has only the entries its blocks use: the theme of each kind of component block, and each text style a block names.
export type DocumentTheme = Partial<ComponentThemes> & {
  h1?: HeadingStyle;
  // Its link colour is every link's, whatever the style around it.
  body?: TextStyle;
  // A signature field's label.
  fieldLabel?: TextStyle;
  // Any other text style a block names, such as "h2" or "muted".
  [style: string]:
    | HeadingStyle
    | ComponentThemes[keyof ComponentThemes]
    | undefined;
};

// The PDF's link colour when the theme sets none.
export const DEFAULT_LINK_COLOR = "#06AB71";

export const linkColorOf = (theme: DocumentTheme) =>
  theme.body?.link ?? DEFAULT_LINK_COLOR;

// The text style a block names, which may carry the heading options; the map's validator makes sure it is there.
export const textStyle = (theme: DocumentTheme, name: string): HeadingStyle => {
  const style = theme[name];
  if (!style) throw new Error(`The theme has no "${name}" style`);
  return style as HeadingStyle;
};

// The theme of a component a block uses; the map's validator makes sure it is there.
export const componentTheme = <K extends keyof ComponentThemes>(
  theme: DocumentTheme,
  kind: K
): ComponentThemes[K] => {
  const entry = theme[kind];
  if (!entry) throw new Error(`The theme has no "${kind}" entry`);
  return entry as ComponentThemes[K];
};

export interface FixedItem extends DocumentField {
  x: number;
  baseline: number;
  width: number;
  font: string;
  size: number;
  color: string;
  // A value may wrap down to this many lines, the last one ending in "…"; later items move down.
  maxLines?: number;
  leading?: number;
}

export interface CardField extends DocumentField {
  label: string;
}

// A text or bullets block's own spacing and line height, over its style's.
interface BlockSpacing {
  spaceBefore?: number;
  spaceAfter?: number;
  leading?: number;
}

// Any flowing block may be one the rep switches on or off; `included` is their choice.
interface Choosable {
  id?: string;
  optional?: boolean;
  included?: boolean;
}

// Fields placed at fixed positions on a page where nothing flows, such as the cover.
export interface FixedBlock {
  type: "fixed";
  items: FixedItem[];
}
// A text block's `style` names any text style in the theme.
export type HeadingBlock = Choosable &
  BlockSpacing & {
    type: "heading";
    style: string;
    text: Inline[];
  };
export type ParagraphBlock = Choosable &
  BlockSpacing & {
    type: "paragraph";
    style: string;
    text: Inline[];
  };
export type ScriptBlock = Choosable &
  BlockSpacing & {
    type: "script";
    style: string;
    text: Inline[];
  };
export type StyledTextBlock = HeadingBlock | ParagraphBlock | ScriptBlock;
// A bullets block's spaceBefore is over its first item, spaceAfter under its last, and leading on every item.
export type BulletsBlock = Choosable &
  BlockSpacing & {
    type: "bullets";
    style: string;
    items: Inline[][];
  };
export type TableBlock = Choosable & {
  type: "table";
  id: string;
  // `suffix` follows each value that isn't blank: "$110" reads "$110 per User/Month".
  columns: {
    label: string;
    source: string;
    align?: "right";
    width?: number;
    suffix?: string;
  }[];
  // A line without a value for a column holds null there.
  rows: Record<string, string | null>[];
  // A table may have no total row.
  total?: DocumentField & { label: string };
  spaceBefore?: number;
  spaceAfter?: number;
};
export type CardBlock = Choosable & {
  type: "card";
  id: string;
  band: CardField[];
  sections: { columns: { title?: string; fields: CardField[] }[] }[];
  spaceBefore?: number;
  spaceAfter?: number;
};
export type SignatureBlock = Choosable & {
  type: "signature";
  signerPosition: number;
  fields: { type: string; label: string }[];
  // Drawn above the fields in the signature theme's title style, as "CUSTOMER".
  title?: string[];
  spaceBefore?: number;
  spaceAfter?: number;
};

export type DocumentBlock =
  | FixedBlock
  | HeadingBlock
  | ParagraphBlock
  | ScriptBlock
  | BulletsBlock
  | TableBlock
  | CardBlock
  | SignatureBlock;

export interface DocumentSection {
  id: string;
  // Drawn as the page heading; numbered sections get "N." from their place among numbered sections.
  title?: Inline[];
  numbered: boolean;
  // A locked section stays in place when sections are reordered.
  locked: boolean;
  pageBreakBefore: boolean;
  background: string;
  blocks: DocumentBlock[];
}

export interface ResolvedDocument {
  templateVersionId: string;
  page: {
    size: [number, number];
    // Bottom-left origin, as in the PDF.
    frame: { x: number; y: number; width: number; height: number };
    // Background name to its page number in the template's background PDF.
    backgrounds: Record<string, number>;
    // A template may have no footer, sent as absent or null.
    footer?: {
      backgrounds: string[];
      font: string;
      size: number;
      color: string;
      left: number;
      right: number;
      baseline: number;
      lineGap: number;
      text: string;
      // The proposal's name, a field the rep can edit.
      name: DocumentField;
    } | null;
  };
  // A template font's name to its file, relative to the template version's files.
  fonts?: Record<string, string>;
  theme: DocumentTheme;
  sections: DocumentSection[];
}

export const isField = (part: Inline): part is DocumentField =>
  typeof part === "object" && "slot" in part;

export const isLink = (part: Inline): part is DocumentLink =>
  typeof part === "object" && "link" in part;

// An optional block the rep has not switched on is not part of the document.
export const isHidden = (block: DocumentBlock) =>
  block.type !== "fixed" && block.optional === true && block.included !== true;

// The id an optional block's on/off choice is kept under; undefined for a block that isn't optional or has no id.
export const choiceId = (block: DocumentBlock) =>
  block.type !== "fixed" && block.optional ? block.id : undefined;
